/**
 * Shift Event Breakdown — shared formatting.
 *
 * Lives in utils rather than in the page folder because `services/csvExport.js`
 * needs the same functions, and a page importing into services (or services
 * importing out of a page) is backwards. Same reason `reportTimeWindow.js` and
 * `activeAlerts.js` sit here.
 *
 * The units split these exist to serve, set by sp_getShiftReport:
 *   *_time     -> MINUTES, already rounded to 1dp by the proc
 *   *_seconds  -> raw integer SECONDS
 *
 * A 4-second minimum response is 0.1 at one decimal minute, which defeats the
 * point of showing that column at all (plan Q2). So the seconds-valued columns
 * are rendered as durations and the minute-valued ones as plain numbers.
 */

/** Shift order and colours, mirroring ShiftLegend.jsx and ShiftSettingsPage.jsx. */
export const SHIFT_ORDER = ['Day', 'Evening', 'Night'];

export const SHIFT_COLORS = {
    Day: '#27B4FF',
    Evening: '#276BFF',
    Night: '#0B16FF',
};

/* Fallback for a shift name that is not one of the three. The settings editor
   only ever writes those, but core_shift_hours is a plain table and
   getMainDashAreaShiftSummary already drops anything else into no bucket at all
   — reading as a zero, with nothing on screen saying so. This report shows an
   unrecognised shift in grey rather than hiding it. */
export const SHIFT_COLOR_UNKNOWN = '#94A3B8';

export const shiftColor = (name) => SHIFT_COLORS[name] || SHIFT_COLOR_UNKNOWN;

/** Sort comparator putting the three known shifts first, in shift order. */
export function compareShifts(a, b) {
    const ia = SHIFT_ORDER.indexOf(a);
    const ib = SHIFT_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return String(a || '').localeCompare(String(b || ''));
}

/** Em dash — the single place the "no value" glyph is decided. */
export const DASH = '—';

/**
 * Raw seconds -> "4s" / "3m 12s" / "1h 04m".
 *
 * Mirrors fmt_duration_seconds in report_generator.py. If either changes, change
 * both: a downloaded PDF and a scheduled one must read the same.
 */
export function formatDurationSeconds(value) {
    if (value === null || value === undefined || value === '') return DASH;
    const total = Math.round(Number(value));
    if (!Number.isFinite(total) || total < 0) return DASH;
    if (total < 60) return `${total}s`;
    if (total < 3600) return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
    return `${Math.floor(total / 3600)}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`;
}

/** Minutes (already 1dp from the proc) -> "3.2", or a dash. */
export function formatMinutes(value) {
    if (value === null || value === undefined || value === '') return DASH;
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(1) : DASH;
}

/** Rate -> "1.79", or a dash. Two decimals, matching rateOrNone. */
export function formatRate(value) {
    if (value === null || value === undefined || value === '') return DASH;
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : DASH;
}

/**
 * Signed percentage for the delta-vs-campus row.
 *
 * The sign is the point: positive means slower than the campus, so a faster
 * shift is not the only one that carries a marker.
 */
export function formatSignedPct(value) {
    if (value === null || value === undefined || value === '') return DASH;
    const n = Number(value);
    if (!Number.isFinite(n)) return DASH;
    return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}

/** Percentage -> "28.0%", or a dash. */
export function formatPct(value) {
    if (value === null || value === undefined || value === '') return DASH;
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(1)}%` : DASH;
}

export const formatCount = (value) => Number(value || 0).toLocaleString();

/** 0-23 -> "12 AM" / "7 AM" / "11 PM". Mirrors _fmt_hour_label in the generator. */
export function formatHourLabel(hour) {
    const h = ((Number(hour) || 0) % 24 + 24) % 24;
    const period = h < 12 ? 'AM' : 'PM';
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display} ${period}`;
}

/** Compact hour for a dense axis: "12a" / "7a" / "11p". */
export function formatHourShort(hour) {
    const h = ((Number(hour) || 0) % 24 + 24) % 24;
    if (h === 0) return '12a';
    if (h === 12) return '12p';
    return h < 12 ? `${h}a` : `${h - 12}p`;
}

/**
 * The 24-hour map -> a human "hours covered" label per shift.
 *
 * Built from the hour rows rather than a proc column because a shift's hours
 * NEED NOT BE CONTIGUOUS — the settings editor lets any hour be any shift, so
 * Day 07-11 + Day 13-15 is a legal configuration. A single start/end pair would
 * describe that wrongly; this emits "7a-12p, 1p-4p" instead.
 *
 * Ranges are inclusive of the start hour and exclusive of the hour after the
 * last, so hours 7..14 read "7a-3p" — the way a shift is written on a rota.
 *
 * @param {Array<{local_hour:number, shift_name:string}>} hours
 * @returns {Object<string,string>} shift name -> label
 */
export function shiftHourLabels(hours) {
    const byShift = {};
    (hours || []).forEach((h) => {
        const name = h?.shift_name;
        if (!name) return;
        if (!byShift[name]) byShift[name] = [];
        byShift[name].push(Number(h.local_hour));
    });

    const out = {};
    Object.keys(byShift).forEach((name) => {
        const list = [...byShift[name]].sort((a, b) => a - b);
        /* A shift that wraps midnight (23, 0, 1, ... 6) sorts as two blocks with
           0 first, which would read "12a-7a, 11p-12a". Rotating so a block
           starting at 0 whose predecessor 23 is also in the set moves to the end
           gives the "11p-7a" a reader expects. */
        const wraps = list.includes(0) && list.includes(23);
        const ordered = wraps
            ? [...list.filter((h) => h >= splitPoint(list)), ...list.filter((h) => h < splitPoint(list))]
            : list;

        const parts = [];
        let runStart = ordered[0];
        let prev = ordered[0];
        for (let i = 1; i <= ordered.length; i += 1) {
            const cur = ordered[i];
            const contiguous = cur === (prev + 1) % 24;
            if (i === ordered.length || !contiguous) {
                parts.push(`${formatHourShort(runStart)}-${formatHourShort((prev + 1) % 24)}`);
                runStart = cur;
            }
            prev = cur;
        }
        out[name] = parts.join(', ');
    });
    return out;
}

/** First hour of the wrapped block: the lowest hour whose predecessor is absent. */
function splitPoint(sortedHours) {
    const set = new Set(sortedHours);
    for (let i = 0; i < sortedHours.length; i += 1) {
        const h = sortedHours[i];
        if (!set.has((h + 23) % 24)) return h;
    }
    return sortedHours[0];
}
